import { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from '@shared/hooks/useDebounce';
import { listQmsCategoryApiService } from '@epics/qmsManagement/api/listQmsCategoryApiService';
import type { FilterOption } from '@shared/components/FilterPopup';
import {
  ALLOWED_LIMITS,
  validateStatusString,
  formatQmsCategoryCreatedAt,
  type FormattedQmsCategory,
} from './qmsCategoryListValidation';
import type { QmsCategory } from '../../types/qmsCategoryTypes';

const DEFAULT_STATUS_OPTIONS: FilterOption[] = [
  { label: 'Aktywny', value: 'active', checked: false },
  { label: 'Nieaktywny', value: 'inactive', checked: false },
];

function formatCategories(categories: QmsCategory[]): FormattedQmsCategory[] {
  return categories.map((cat) => ({
    id: cat.id,
    name: cat.name,
    description: cat.description,
    tags: cat.tags,
    status: cat.status,
    createdAt: cat.createdAt,
    formattedCreatedAt: formatQmsCategoryCreatedAt(cat.createdAt),
  }));
}

function parseStatusOptions(statusParam: string | null): FilterOption[] {
  if (!statusParam || !validateStatusString(statusParam)) {
    return [...DEFAULT_STATUS_OPTIONS];
  }
  const statusValues = statusParam.split(',').map((s) => s.trim());
  return [
    {
      label: 'Aktywny',
      value: 'active',
      checked: statusValues.includes('active'),
    },
    {
      label: 'Nieaktywny',
      value: 'inactive',
      checked: statusValues.includes('inactive'),
    },
  ];
}

export const useQmsCategoryList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isSyncingFromUrl = useRef(false);

  const [categories, setCategories] = useState<FormattedQmsCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(() => {
    const v = parseInt(searchParams.get('page') ?? '1', 10);
    return isNaN(v) || v < 1 ? 1 : v;
  });
  const [limit, setLimit] = useState(() => {
    const v = parseInt(searchParams.get('limit') ?? '10', 10);
    return (ALLOWED_LIMITS as readonly number[]).includes(v) ? v : 10;
  });
  const [totalCount, setTotalCount] = useState(0);
  const [isNoFilterFetch, setIsNoFilterFetch] = useState(false);
  const [searchValue, setSearchValue] = useState(() => {
    const v = (searchParams.get('search') ?? '').trim();
    return v.length > 50 ? v.substring(0, 50) : v;
  });
  const [limitError, setLimitError] = useState('');
  const [fetchError, setFetchError] = useState('');
  const [accessError, setAccessError] = useState(false);

  const debouncedSearch = useDebounce(searchValue, 500);
  const prevSearchRef = useRef(searchValue);
  const prevDebouncedSearchRef = useRef(debouncedSearch);

  const [statusOptions, setStatusOptions] = useState<FilterOption[]>(() =>
    parseStatusOptions(searchParams.get('status'))
  );
  const [appliedFilters, setAppliedFilters] = useState<{
    statusOptions: FilterOption[];
  }>(() => ({
    statusOptions: parseStatusOptions(searchParams.get('status')),
  }));

  // ── URL change → state sync ──────────────────────────────────────────────
  useEffect(() => {
    isSyncingFromUrl.current = true;

    const rawPage = parseInt(searchParams.get('page') ?? '1', 10);
    const nextPage = isNaN(rawPage) || rawPage < 1 ? 1 : rawPage;
    const rawLimit = parseInt(searchParams.get('limit') ?? '10', 10);
    const nextLimit = (ALLOWED_LIMITS as readonly number[]).includes(rawLimit)
      ? rawLimit
      : 10;
    const rawSearch = (searchParams.get('search') ?? '').trim();
    const nextSearch =
      rawSearch.length > 50 ? rawSearch.substring(0, 50) : rawSearch;
    const nextStatusOptions = parseStatusOptions(searchParams.get('status'));

    setPage((prev) => (prev !== nextPage ? nextPage : prev));
    setLimit((prev) => (prev !== nextLimit ? nextLimit : prev));
    setSearchValue((prev) => (prev !== nextSearch ? nextSearch : prev));
    setStatusOptions((prev) => {
      if (
        prev[0]?.checked === nextStatusOptions[0]?.checked &&
        prev[1]?.checked === nextStatusOptions[1]?.checked
      ) {
        return prev;
      }
      return nextStatusOptions;
    });
    setAppliedFilters((prev) => {
      if (
        prev.statusOptions[0]?.checked === nextStatusOptions[0]?.checked &&
        prev.statusOptions[1]?.checked === nextStatusOptions[1]?.checked
      ) {
        return prev;
      }
      return { statusOptions: nextStatusOptions };
    });
  }, [searchParams]);

  // Reset sync flag after state settles
  useEffect(() => {
    if (isSyncingFromUrl.current) {
      isSyncingFromUrl.current = false;
    }
  }, [page, limit, searchValue, statusOptions, appliedFilters]);

  // ── State → URL sync ─────────────────────────────────────────────────────
  useEffect(() => {
    if (isSyncingFromUrl.current) {
      prevSearchRef.current = searchValue;
      return;
    }
    const params = new URLSearchParams();
    const searchChanged = prevSearchRef.current.trim() !== searchValue.trim();
    if (searchChanged) {
      prevSearchRef.current = searchValue;
      params.set('page', '1');
      if (page !== 1) {
        setPage(1);
      }
    } else {
      params.set('page', page.toString());
    }
    params.set('limit', limit.toString());
    if (searchValue) {
      params.set('search', searchValue);
    }
    const selectedStatuses = appliedFilters.statusOptions
      .filter((o) => o.checked)
      .map((o) => o.value)
      .join(',');
    if (selectedStatuses) {
      params.set('status', selectedStatuses);
    }
    if (
      params.toString() !==
      new URLSearchParams(searchParams.toString()).toString()
    ) {
      setSearchParams(params, { replace: false });
    }
  }, [page, limit, searchValue, appliedFilters, setSearchParams, searchParams]);

  // ── API fetch ────────────────────────────────────────────────────────────
  const fetchCategories = useCallback(async () => {
    let didRedirect = false;
    try {
      setLoading(true);
      setFetchError('');
      const selectedStatuses = appliedFilters.statusOptions
        .filter((o) => o.checked)
        .map((o) => o.value)
        .join(',');
      const trimmedSearch = debouncedSearch.trim();

      const response = await listQmsCategoryApiService.listCategories({
        page,
        limit,
        ...(trimmedSearch && { search: trimmedSearch }),
        ...(selectedStatuses && { status: selectedStatuses }),
      });

      if (!response.success) {
        setFetchError(
          response.error ?? 'Wystąpił błąd podczas ładowania danych.'
        );
        setCategories([]);
        setTotalCount(0);
        return;
      }

      if (
        response.totalCount > 0 &&
        response.totalPages > 0 &&
        page > response.totalPages
      ) {
        didRedirect = true;
        setPage(response.totalPages);
        return;
      }

      setTotalCount(response.totalCount);
      setIsNoFilterFetch(!trimmedSearch && !selectedStatuses);
      setCategories(formatCategories(response.data));
    } catch (error) {
      if ((error as { status?: number }).status === 403) {
        setAccessError(true);
      } else {
        setFetchError('Wystąpił błąd podczas ładowania danych.');
        setCategories([]);
        setTotalCount(0);
      }
    } finally {
      if (!didRedirect) {
        setLoading(false);
      }
    }
  }, [page, limit, debouncedSearch, appliedFilters]);

  // Reset page when debounced search changes
  useEffect(() => {
    if (isSyncingFromUrl.current) {
      prevDebouncedSearchRef.current = debouncedSearch;
      return;
    }
    if (prevDebouncedSearchRef.current.trim() !== debouncedSearch.trim()) {
      prevDebouncedSearchRef.current = debouncedSearch;
      if (page !== 1) {
        setPage(1);
      }
    }
  }, [debouncedSearch, page]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const isSearchClearing = !searchValue.trim() && debouncedSearch.trim() !== '';

  const justClearedSearch =
    prevDebouncedSearchRef.current.trim() !== '' &&
    debouncedSearch.trim() === '' &&
    !appliedFilters.statusOptions.some((o) => o.checked);

  const setSearch = useCallback(
    (newSearch: string) => {
      if (newSearch.length > 50) {
        return;
      }
      if (!newSearch.trim() && searchValue.trim()) {
        setLoading(true);
      }
      setSearchValue(newSearch);
    },
    [searchValue]
  );

  const handleApplyFilters = useCallback(() => {
    const selectedStatuses = statusOptions
      .filter((o) => o.checked)
      .map((o) => o.value)
      .join(',');
    if (selectedStatuses && !validateStatusString(selectedStatuses)) {
      return;
    }
    setAppliedFilters({ statusOptions });
    setPage(1);
  }, [statusOptions]);

  const handleClearFilters = useCallback(() => {
    const hasFilters = appliedFilters.statusOptions.some((o) => o.checked);
    setStatusOptions([...DEFAULT_STATUS_OPTIONS]);
    if (hasFilters) {
      setAppliedFilters({ statusOptions: [...DEFAULT_STATUS_OPTIONS] });
      setPage(1);
    }
  }, [appliedFilters]);

  const handleLimitChange = useCallback((newLimit: number) => {
    if (!(ALLOWED_LIMITS as readonly number[]).includes(newLimit)) {
      setLimitError('Dozwolone wartości: 10, 30, 50, 100.');
      return;
    }
    setLimitError('');
    setLimit(newLimit);
    setPage(1);
  }, []);

  return {
    categories,
    loading,
    page,
    setPage,
    limit,
    totalCount,
    isNoFilterFetch,
    search: searchValue,
    isSearchClearing: isSearchClearing || justClearedSearch,
    setSearch,
    statusOptions,
    setStatusOptions,
    appliedFilters,
    handleApplyFilters,
    handleClearFilters,
    fetchError,
    setFetchError,
    accessError,
    limitError,
    handleLimitChange,
    refetch: fetchCategories,
  };
};
